import api from './api'
import type {
  DashboardBoard,
  DashboardLayoutPreference,
  DashboardLayoutsResponse,
  DashboardPayload,
  DrillRequest,
  DrillResult,
} from '@/types/dashboards'

/** Response payloads are used verbatim — the panel and column keys ARE the
 *  API's field names, so no camel-case transform runs over them. */

export type DrillExportOutcome = {
  blob: Blob
  filename: string
  rows: number
}

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

function filenameFrom(disposition: string | undefined, fallback: string): string {
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/.exec(disposition ?? '')
  return match?.[1] ?? fallback
}

function slug(dataset: string): string {
  return dataset.replace(/[^a-z0-9]+/gi, '-').toLowerCase()
}

/** What one ratio check was computed on (`exceptions.check_basis`): the
 *  screen's input and whether it is a stand-in for the stabilized figure the
 *  program tests (loan / purchase price, in-place NOI). */
export interface CreditBoxCheckBasis {
  check: string
  input: string
  selectedFrom: string
  label: string
  value: number | null
  standIn: boolean
  text: string
}

/** One deal's credit-box reading (`services/dashboards/exceptions.py`). */
export interface CreditBoxReading {
  source: 'credit_box'
  program: string | null
  programName: string | null
  rulesVersion: string | null
  outcome: string | null
  exceptions: string[]
  missingInputs: number
  basis?: CreditBoxCheckBasis[]
  rulesStatus?: string | null
  /** Set while the ruleset is not ratified: 'Credit Box v2.0 — awaiting client sign-off'. */
  rulesNote?: string | null
  screenBasis?: string | null
  screenedAt?: string | null
}

export interface DealExceptionsResponse {
  definition: string
  /** Keyed by loan id. Not camel-cased on read: the keys are deal ids. */
  loans: Record<string, CreditBoxReading>
  screened: number
  rulesVersion?: string | null
  rulesStatus?: string | null
  rulesNote?: string | null
}

export const dashboardsService = {
  getBoard: (board: DashboardBoard, signal?: AbortSignal) =>
    api.get<DashboardPayload>(`/dashboards/${board}`, { signal }).then((r) => r.data),

  getDealExceptions: (signal?: AbortSignal) =>
    api.get<DealExceptionsResponse>('/dashboards/exceptions', { signal }).then((r) => r.data),

  getLayouts: (board: DashboardBoard, signal?: AbortSignal) =>
    api.get<DashboardLayoutsResponse>(`/dashboards/layouts/${board}`, { signal }).then((r) => r.data),

  saveLayout: (
    board: DashboardBoard,
    view: string,
    layout: DashboardLayoutPreference,
  ) => api.put<{
    board: DashboardBoard
    view: string
    layout: DashboardLayoutPreference
    saved: boolean
  }>(`/dashboards/layouts/${board}/${view}`, layout).then((r) => r.data),

  resetLayout: (board: DashboardBoard, view: string) =>
    api.delete<{
      board: DashboardBoard
      view: string
      layout: null
      saved: boolean
    }>(`/dashboards/layouts/${board}/${view}`).then((r) => r.data),

  drill: (request: DrillRequest, signal?: AbortSignal) =>
    api.post<DrillResult>('/dashboards/drill', request, { signal }).then((r) => r.data),

  /** Same record set as `drill`, as a workbook. Paging is deliberately not
   *  sent — an export of page 2 of 40 is a bug report waiting to happen. */
  drillExport: async (
    request: Omit<DrillRequest, 'page' | 'pageSize'>,
  ): Promise<DrillExportOutcome> => {
    const response = await api.post('/dashboards/drill/export', request, {
      responseType: 'blob',
    })
    const blob = response.data as Blob

    // An error page saved as .xlsx is worse than a clear failure.
    const head = String.fromCharCode(...new Uint8Array(await blob.slice(0, 2).arrayBuffer()))
    if (!head.startsWith('PK')) {
      throw new Error(`server returned ${blob.type || 'unknown content'} instead of a workbook`)
    }

    return {
      blob: blob.type ? blob : new Blob([blob], { type: XLSX_MIME }),
      filename: filenameFrom(
        response.headers['content-disposition'],
        `${slug(request.dataset)}_${new Date().toISOString().slice(0, 10)}.xlsx`,
      ),
      rows: Number(response.headers['x-export-rows'] ?? 0),
    }
  },
}

/** Hand a generated file to the browser without leaking the object URL. */
export function downloadDrillExport(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.rel = 'noopener'
  document.body.appendChild(link)
  link.click()
  link.remove()
  setTimeout(() => URL.revokeObjectURL(url), 30_000)
}
